import { Component, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { Table } from 'primeng/table';
import { Observable, Subject } from 'rxjs';
import { finalize, first, take, takeUntil } from 'rxjs/operators';
import _ from 'lodash';

import { Account } from '../../models/account';
import { AccountFaq, AccountFaqCategory } from '../../models/faq';
import { AccountService } from '../../services/account.service';
import { ModalService } from '../../services/modal.service';
import { SiteService } from '../../services/site.service';

import { AccountFaqComponent } from '../account-faq/account-faq.component';
import { AccountFaqCategoriesComponent } from '../account-faq-categories/account-faq-categories.component';

/**
 * Account → FAQs — list view + CRUD for the CMS FAQ bank rendered on
 * picaluna.com. Mirrors AccountFuneralCostListComponent: id-keyed collection
 * under account.data.faqs, persisted per-item through the RBAC-gated
 * content route (accountService.patchContentItem('faqs', …)).
 *
 * Each FAQ carries a `pages` list — ONE item can render on SEVERAL
 * picaluna.com pages; the site's <FaqOverview> filters by page URL.
 */
const ALL_CATEGORIES = { label: 'All categories', value: null };

/** An account's FAQ categories (content.faqCategories), live ones, display order. */
function categoriesOf(account: Account | undefined): AccountFaqCategory[] {
  const raw = (account as any)?.data?.content?.faqCategories;
  const list: AccountFaqCategory[] = (Array.isArray(raw) ? raw : Object.values(raw || {}))
    .filter((c: any) => c && typeof c === 'object' && c?._meta?.id && !c?._meta?.deleted);
  return list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

@Component({
  selector: 'app-account-faq-list',
  templateUrl: './account-faq-list.component.html',
  styleUrls: ['./account-faq-list.component.scss'],
})
export class AccountFaqListComponent implements OnInit, OnDestroy {
  /** True until ensureFullAccount() resolves (see content lists). */
  accountLoading = true;

  private unsubscribe$ = new Subject<void>();

  account$: Observable<Account>;

  /** Category filter (#220): "All categories" + the categories, rebuilt when they're edited (the Categories dialog). */
  categoryFilterOptions: { label: string; value: string | null }[] = [ALL_CATEGORIES];
  selectedCategory: string | null = null;

  constructor(
    private siteService: SiteService,
    private modalService: ModalService,
    private accountService: AccountService,
    private dialogService: DialogService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.account$ = this.accountService.account$;
  }

  ngOnInit(): void {
    // The default account fetch omits the content subtree (payload diet) —
    // load the full account so account.data.faqs is present.
    this.accountService.ensureFullAccount().pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: () => (this.accountLoading = false),
      error: () => (this.accountLoading = false),
    });
    this.account$.pipe(takeUntil(this.unsubscribe$)).subscribe((account) => {
      this.categoryFilterOptions = [
        ALL_CATEGORIES,
        ...categoriesOf(account).map((c) => ({ label: c.name || c._meta.id, value: c._meta.id })),
      ];
    });
  }

  /** Filter the table to one category (or clear it: "All categories"/null). */
  onCategoryFilter(table: Table, value: string | null): void {
    table.filter(value, 'category', 'equals');
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /** account.data.faqs as a stable sorted array (soft-deleted kept, for restore). */
  faqsAsList(data: { [id: string]: AccountFaq } | AccountFaq[] | undefined): AccountFaq[] {
    if (!data) return [];
    const list = Array.isArray(data) ? data.filter(Boolean) : Object.values(data).filter(Boolean);
    return _.sortBy(list, (f) => f?._meta?.index ?? Number.MAX_SAFE_INTEGER);
  }

  /**
   * The /faq hub category, for the table's second column. Replaced "Shown on
   * pages" (#202): pages now name the FAQs they want, so listing an FAQ's
   * pages here would show data nobody can edit from this screen.
   */
  categoryLabel(faq: AccountFaq): string {
    if (!faq.category) return '—';
    const match = this.categories().find((c) => c._meta.id === faq.category);
    // A category can be deleted while items still point at it; show the
    // dangling id rather than an em dash, so it reads as "needs attention"
    // instead of "none".
    return match?.name || faq.category;
  }

  /** Category records, display order. */
  categories(): AccountFaqCategory[] {
    return categoriesOf(this.lastAccountSnapshot());
  }

  onEditCategories(): void {
    this.dialogService.open(AccountFaqCategoriesComponent, {
      header: 'FAQ categories',
      width: '44rem',
      baseZIndex: 11000,
    });
    // Nothing to do on close: each category write emits a fresh account, and
    // the table reads categories off that snapshot.

  }

  /** Whether the item appears in a full-page FAQ block (absent = yes). */
  onFaqPage(faq: AccountFaq): boolean {
    return faq.onFaqPage !== false;
  }


  onAddFaq(event: Event): void {
    const id = `id-faq-${this.shortId()}`;
    const account = this.lastAccountSnapshot();
    if (!account) return;
    const existing = this.faqsAsList(account.data?.content?.faqs);
    const seed: AccountFaq = {
      _meta: { id, index: this.computeNextIndex(existing) },
      question: '',
      answer: '',
      pages: [],
    };
    this.openEditor(seed, true);
  }

  onEditFaq(event: Event, faq: AccountFaq): void {
    this.openEditor(_.cloneDeep(faq), false);
  }

  onDeleteFaq(event: Event, faq: AccountFaq): void {
    const modal = this.modalService.generic({
      title: 'Remove FAQ',
      copy: [
        `Are you sure you want to remove "${faq.question || faq._meta.id}"?`,
        'It will stop appearing on picaluna.com after the next site publish.',
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: '' },
        { label: 'Confirm', key: 'confirm', class: 'p-button-danger', icon: 'pi pi-trash' },
      ],
    });

    modal.onClose.subscribe({
      next: (reason: string) => {
        if (reason?.toString().toLowerCase() === 'confirm') {
          faq._meta.deleted = true;
          this.persistItem(faq);
        }
      },
    });
  }

  onRestoreFaq(event: Event, faq: AccountFaq): void {
    faq._meta.deleted = false;
    this.persistItem(faq);
  }

  /** Open the dedicated editor; on save, write back + persist (funeral-costs pattern). */
  private openEditor(faq: AccountFaq, isNew: boolean): void {
    const ref = this.dialogService.open(AccountFaqComponent, {
      // Esc would close the editor and silently discard unsaved changes.
      closeOnEscape: false,
      header: isNew ? 'Add FAQ' : 'Edit FAQ',
      width: '48rem',
      baseZIndex: 11000, // above the outer list modal
      data: {
        faq,
        isNew,
        categoryOptions: this.categories(),
        // Editor-performed save: it closes only once the server confirms
        // (Save spins meanwhile), so a publish cannot race the write.
        save: (f: AccountFaq) => this.accountService.patchContentItem('faqs', f).pipe(take(1)),
      },
    });

    ref.onClose.subscribe({
      next: (result: { saved: boolean; faq: AccountFaq; persisted?: boolean } | undefined) => {
        if (!result?.saved) return;
        const account = this.lastAccountSnapshot();
        if (!account) return;

        // account.data on the client is the post-objectsToArrays shape —
        // collections are ARRAYS here; patch() inverts back to id-keyed
        // objects. Array ops only, or the new item drops (funeral-costs lesson).
        // FAQs live under the content namespace: account.data.content.faqs
        // (the server's content-kind base path).
        if (!account.data.content || typeof account.data.content !== 'object') {
          account.data.content = {};
        }
        if (!Array.isArray(account.data.content.faqs)) {
          account.data.content.faqs = [];
        }
        const faqs = account.data.content.faqs as AccountFaq[];
        const existingIndex = faqs.findIndex((f) => f?._meta?.id === result.faq._meta.id);
        if (existingIndex >= 0) {
          faqs[existingIndex] = result.faq;
        } else {
          faqs.push(result.faq);
        }
        if (!result.persisted) this.persistItem(result.faq);
      },
    });
  }

  /** Per-item persistence via the RBAC-gated content route (kind 'faqs'). */
  private persistItem(faq: AccountFaq): void {
    this.siteService.addSubscriptionLog(
      this,
      'account-faq-list.component.ts->persistItem->this.accountService.patchContentItem'
    );

    this.accountService
      .patchContentItem('faqs', faq)
      .pipe(
        finalize(() =>
          this.siteService.setSubscriptionLogFinalised(
            'account-faq-list.component.ts->persistItem->this.accountService.patchContentItem'
          )
        ),
        // take(1), NOT takeUntil(unsubscribe$) + first(). takeUntil on a WRITE
        // aborts the in-flight save when the modal closes AND completes the
        // stream empty, so first() throws EmptyError — the "no elements in
        // sequence" dialog. A content write must outlive its dialog; take(1)
        // completes on its own, so nothing leaks. (Fixed everywhere else
        // 2026-08-20; these two were missed.)
        take(1)
      )
      .subscribe({
        // Silence here meant a server rejection looked exactly like a
        // successful save — the row stayed on screen and the next reload
        // brought back the old value.
        error: (err: any) => this.modalService.error(err?.error?.error || err),
      });
  }

  private lastAccountSnapshot(): Account | undefined {
    let snapshot: Account | undefined;
    this.accountService.account$.pipe(first()).subscribe({
      next: (a) => (snapshot = a),
    });
    return snapshot;
  }

  /** Next index with gaps of 10 (Conductor convention for reorderable lists). */
  private computeNextIndex(existing: AccountFaq[]): number {
    const max = existing.reduce((acc, f) => {
      const i = f?._meta?.index;
      return typeof i === 'number' && i > acc ? i : acc;
    }, -10);
    return max + 10;
  }

  /** Short, URL-safe id suffix — random 8 chars. */
  private shortId(): string {
    return Math.random().toString(36).slice(2, 10);
  }
}
